import { createContext, useContext } from 'react';
import type { Product } from '../../../contexts/AuthContext';

// Re-export types that the team panels need
export type { Product };

export interface OrgRbacMemberProduct {
  productId: string;
  name: string;
  jiraProjectKey: string | null;
}

export interface OrgRbacMemberRow {
  user_id: string;
  role: string;
  joined_at?: string | null;
  email?: string | null;
  email_confirmed_at?: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
  products?: OrgRbacMemberProduct[] | null;
}

export interface TeamMember {
  id?: number;
  name: string;
  email?: string;
  jiraUsername?: string;
  capacityHoursPerDay: number;
  isActive: boolean;
  includeInCapacity?: boolean;
  role?: string;
  jiraActive?: boolean | null;
  includeInHistoricalCapacity?: boolean;
  startDateInTeam?: string | null;
  endDateInTeam?: string | null;
  distributionCriteriaType?: string | null;
  distributionCriteriaValues?: string[] | null;
  distributionCriteria?: {
    cluster?: string[];
    component: string[];
    label: string[];
    priority: string[];
    customFields?: Record<string, string[]>;
  } | null;
  excludeFromDistribution?: boolean;
  personId?: string;
}

export interface ProjectSettings {
  estimationUnit: 'hours_per_day' | 'story_points_per_day';
  estimationHoursPerDay: number;
  workingDays: number[];
}

export interface TeamSuggestionsData {
  candidates: Array<{
    displayName: string;
    totalTouches: number;
    lastSeen: string | null;
    touchesByProduct: Record<string, number>;
  }>;
  existingMatches: Array<{
    displayName: string;
    totalTouches: number;
    lastSeen: string | null;
    touchesByProduct: Record<string, number>;
    existingPersonId: string;
  }>;
  products: Array<{ id: string; name: string; jiraProjectKey: string | null }>;
  filters: { sinceMonths: number; minTouches: number; sinceCutoff: string };
}

export interface CriteriaOptions {
  components: string[];
  labels: string[];
  priorities: string[];
  customFields: Array<{
    orgFieldId: number;
    jiraFieldId: string;
    displayName: string;
    distinctValues: string[];
  }>;
}

export interface RoleItem {
  id: number;
  name: string;
  includeInCapacityMapping?: boolean;
  capacityCategory?: string;
}

export type RosterSortBy = 'name' | 'role' | 'capacity';
export type RosterSortDir = 'asc' | 'desc';

/** Full context surface for the Team tab. ProfilePage is the provider. */
export interface TeamTabContextValue {
  // Auth / org
  currentOrg: { id?: string; role?: string; name?: string } | null;
  currentProduct: { id?: string; name?: string } | null;
  effectiveRole: string | null | undefined;
  isSupportUser: boolean;
  canViewTeam: boolean;
  canModifyTeam: boolean;
  /** Owner-only zones: Team access, capacity foundations, roster & suggestions */
  canManageOrgTeam: boolean;
  canViewOrgSettings: boolean;
  visibleProductsSettings: Product[];
  profileApiFetch: (path: string, init?: RequestInit) => Promise<Response>;
  refreshUser: () => Promise<unknown>;

  // Team members / roster
  teamMembers: TeamMember[];
  setTeamMembers: React.Dispatch<React.SetStateAction<TeamMember[]>>;
  originalTeamMembers: TeamMember[];
  setOriginalTeamMembers: React.Dispatch<React.SetStateAction<TeamMember[]>>;
  loadingTeamMembers: boolean;
  setLoadingTeamMembers: React.Dispatch<React.SetStateAction<boolean>>;
  reloadTeamMembers: () => Promise<void>;
  rosterPersonsList: Record<string, unknown>[];
  setRosterPersonsList: React.Dispatch<React.SetStateAction<Record<string, unknown>[]>>;
  rosterFetchError: string | null;
  setRosterFetchError: React.Dispatch<React.SetStateAction<string | null>>;

  // Team suggestions
  teamSuggestionsLoading: boolean;
  setTeamSuggestionsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  teamSuggestionsError: string | null;
  setTeamSuggestionsError: React.Dispatch<React.SetStateAction<string | null>>;
  teamSuggestionsData: TeamSuggestionsData | null;
  setTeamSuggestionsData: React.Dispatch<React.SetStateAction<TeamSuggestionsData | null>>;
  teamSuggestionSelected: Set<string>;
  setTeamSuggestionSelected: React.Dispatch<React.SetStateAction<Set<string>>>;
  teamSuggestionProductOverrides: Record<string, string[]>;
  setTeamSuggestionProductOverrides: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  expandedSuggestions: Set<string>;
  setExpandedSuggestions: React.Dispatch<React.SetStateAction<Set<string>>>;
  handleFetchTeamSuggestions: () => Promise<void>;
  handleBulkAddTeamSuggestions: () => Promise<void>;

  // Saving
  savingCapacity: boolean;
  setSavingCapacity: React.Dispatch<React.SetStateAction<boolean>>;
  teamRosterSaved: boolean;
  setTeamRosterSaved: React.Dispatch<React.SetStateAction<boolean>>;
  teamRosterSaveError: string | null;
  setTeamRosterSaveError: React.Dispatch<React.SetStateAction<string | null>>;

  // Add member
  newMember: Partial<TeamMember>;
  setNewMember: React.Dispatch<React.SetStateAction<Partial<TeamMember>>>;
  addMemberRosterPick: Record<string, unknown> | null;
  setAddMemberRosterPick: React.Dispatch<React.SetStateAction<Record<string, unknown> | null>>;
  addMemberSearchResults: Array<Record<string, unknown>>;
  setAddMemberSearchResults: React.Dispatch<React.SetStateAction<Array<Record<string, unknown>>>>;
  addMemberSearchLoading: boolean;
  setAddMemberSearchLoading: React.Dispatch<React.SetStateAction<boolean>>;
  handleAddTeamMember: () => Promise<void>;
  handleDeleteTeamMember: (id: number) => Promise<void>;
  handleUpdateTeamMember: (member: TeamMember) => Promise<void>;
  persistPersonProductIds: (person: Record<string, unknown>, productIds: string[]) => Promise<void>;

  // Roles
  availableRoles: string[];
  setAvailableRoles: React.Dispatch<React.SetStateAction<string[]>>;
  rolesList: RoleItem[];
  setRolesList: React.Dispatch<React.SetStateAction<RoleItem[]>>;
  showAddRoleInput: boolean;
  setShowAddRoleInput: React.Dispatch<React.SetStateAction<boolean>>;
  showManageRoles: boolean;
  setShowManageRoles: React.Dispatch<React.SetStateAction<boolean>>;
  newRoleName: string;
  setNewRoleName: React.Dispatch<React.SetStateAction<string>>;
  editingRoleId: number | null;
  setEditingRoleId: React.Dispatch<React.SetStateAction<number | null>>;
  editingRoleName: string;
  setEditingRoleName: React.Dispatch<React.SetStateAction<string>>;
  newRoleInputRef: React.RefObject<HTMLInputElement | null>;
  renameInputRef: React.RefObject<HTMLInputElement | null>;
  loadRoles: () => Promise<void>;
  handleToggleRoleInMapping: (roleId: number, included: boolean) => Promise<void>;
  handleChangeCapacityCategory: (roleId: number, category: string) => Promise<void>;
  handleRenameRole: (roleId: number, newName: string) => Promise<void>;
  handleDeleteRole: (roleId: number) => Promise<void>;

  // Invite to org
  inviteEmail: string;
  setInviteEmail: React.Dispatch<React.SetStateAction<string>>;
  inviteRole: 'administrator' | 'member';
  setInviteRole: React.Dispatch<React.SetStateAction<'administrator' | 'member'>>;
  inviteLoading: boolean;
  setInviteLoading: React.Dispatch<React.SetStateAction<boolean>>;
  inviteMessage: string | null;
  setInviteMessage: React.Dispatch<React.SetStateAction<string | null>>;
  inviteError: string | null;
  setInviteError: React.Dispatch<React.SetStateAction<string | null>>;
  inviteOrgProductIds: string[];
  setInviteOrgProductIds: React.Dispatch<React.SetStateAction<string[]>>;
  inviteRoleOptions: Array<{ value: string; label: string }>;
  showInviteSection: boolean;
  handleInviteOrgMember: () => Promise<void>;

  // Org RBAC
  orgRbacMembers: OrgRbacMemberRow[];
  setOrgRbacMembers: React.Dispatch<React.SetStateAction<OrgRbacMemberRow[]>>;
  orgRbacLoading: boolean;
  setOrgRbacLoading: React.Dispatch<React.SetStateAction<boolean>>;
  orgRbacError: string | null;
  setOrgRbacError: React.Dispatch<React.SetStateAction<string | null>>;
  orgRbacRoleDraft: Record<string, string>;
  setOrgRbacRoleDraft: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  orgRbacSavingUserId: string | null;
  setOrgRbacSavingUserId: React.Dispatch<React.SetStateAction<string | null>>;
  orgRbacOwnershipConfirm: { userId: string; displayLabel: string } | null;
  setOrgRbacOwnershipConfirm: React.Dispatch<
    React.SetStateAction<{ userId: string; displayLabel: string } | null>
  >;
  loadOrgRbacMembers: () => Promise<void>;
  handleOrgRbacSaveRow: (targetUserId: string) => void;
  executeOrgRbacPatch: (userId: string, newRole: string) => Promise<void>;
  orgMemberRemoveTarget: OrgRbacMemberRow | null;
  setOrgMemberRemoveTarget: React.Dispatch<React.SetStateAction<OrgRbacMemberRow | null>>;
  orgMemberRemoveLoading: boolean;
  setOrgMemberRemoveLoading: React.Dispatch<React.SetStateAction<boolean>>;
  executeRemoveOrgMember: () => Promise<void>;

  // Project settings (estimation + work schedule)
  projectSettings: ProjectSettings;
  setProjectSettings: React.Dispatch<React.SetStateAction<ProjectSettings>>;
  loadingProjectSettings: boolean;
  setLoadingProjectSettings: React.Dispatch<React.SetStateAction<boolean>>;
  savingProjectSettings: boolean;
  setSavingProjectSettings: React.Dispatch<React.SetStateAction<boolean>>;
  projectSettingsSaved: boolean;
  setProjectSettingsSaved: React.Dispatch<React.SetStateAction<boolean>>;
  projectSettingsSaveError: string | null;
  setProjectSettingsSaveError: React.Dispatch<React.SetStateAction<string | null>>;
  saveProjectSettings: () => Promise<void>;

  // Roster search / sort / page
  rosterSearch: string;
  setRosterSearch: React.Dispatch<React.SetStateAction<string>>;
  rosterSortBy: RosterSortBy;
  setRosterSortBy: React.Dispatch<React.SetStateAction<RosterSortBy>>;
  rosterSortDir: RosterSortDir;
  setRosterSortDir: React.Dispatch<React.SetStateAction<RosterSortDir>>;
  rosterPage: number;
  setRosterPage: React.Dispatch<React.SetStateAction<number>>;
  rosterPageSize: number;

  // Roster invite modal
  rosterInviteModalPerson: Record<string, unknown> | null;
  setRosterInviteModalPerson: React.Dispatch<React.SetStateAction<Record<string, unknown> | null>>;
  rosterInviteEmail: string;
  setRosterInviteEmail: React.Dispatch<React.SetStateAction<string>>;
  rosterInviteRole: 'administrator' | 'member';
  setRosterInviteRole: React.Dispatch<React.SetStateAction<'administrator' | 'member'>>;
  rosterInviteProductIds: string[];
  setRosterInviteProductIds: React.Dispatch<React.SetStateAction<string[]>>;
  rosterInviteLoading: boolean;
  setRosterInviteLoading: React.Dispatch<React.SetStateAction<boolean>>;
  rosterInviteError: string | null;
  setRosterInviteError: React.Dispatch<React.SetStateAction<string | null>>;
  openRosterInviteForPerson: (person: Record<string, unknown>) => void;
  handleSubmitRosterInvite: () => Promise<void>;

  // Distribution criteria
  criteriaOptions: CriteriaOptions;
  setCriteriaOptions: React.Dispatch<React.SetStateAction<CriteriaOptions>>;
  openCriteriaDropdown: string | null;
  setOpenCriteriaDropdown: React.Dispatch<React.SetStateAction<string | null>>;
  criteriaSearchQueries: Record<string, string>;
  setCriteriaSearchQueries: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  criteriaDropdownRef: React.RefObject<HTMLDivElement | null>;
  savingDistributionCriteria: boolean;
  setSavingDistributionCriteria: React.Dispatch<React.SetStateAction<boolean>>;
  distributionCriteriaSaved: boolean;
  setDistributionCriteriaSaved: React.Dispatch<React.SetStateAction<boolean>>;
  distributionCriteriaSaveError: string | null;
  setDistributionCriteriaSaveError: React.Dispatch<React.SetStateAction<string | null>>;

  // Roster product toggle
  rosterProductToggleUserId: string | null;
  setRosterProductToggleUserId: React.Dispatch<React.SetStateAction<string | null>>;

  // Section refs (for deep-link scroll)
  estimationUnitsSectionRef: React.RefObject<HTMLDivElement | null>;
  teamRosterSectionRef: React.RefObject<HTMLDivElement | null>;
  teamAddMemberSectionRef: React.RefObject<HTMLDivElement | null>;
  distributionCriteriaSectionRef: React.RefObject<HTMLDivElement | null>;

  // Highlight state (deep-link pulses)
  highlightEstimationUnitsSection: boolean;
  setHighlightEstimationUnitsSection: React.Dispatch<React.SetStateAction<boolean>>;
  highlightTeamRosterSection: boolean;
  setHighlightTeamRosterSection: React.Dispatch<React.SetStateAction<boolean>>;
  highlightTeamAddMemberSection: boolean;
  setHighlightTeamAddMemberSection: React.Dispatch<React.SetStateAction<boolean>>;
  highlightDistributionCriteriaSection: boolean;
  setHighlightDistributionCriteriaSection: React.Dispatch<React.SetStateAction<boolean>>;
}

export const TeamTabContext = createContext<TeamTabContextValue | null>(null);

export function useTeamTab(): TeamTabContextValue {
  const ctx = useContext(TeamTabContext);
  if (!ctx) {
    throw new Error('useTeamTab must be used within a <TeamTabContext.Provider>');
  }
  return ctx;
}
